import React, { useCallback } from 'react';
import { useIntl } from "react-intl";
import FileSaver from "file-saver";
import bytes from "bytes";
import * as XLSX from "xlsx";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { isCJKCharacter } from '@/util/stringUtil';
import { errorToast } from "@/component/Toast";
import FileDropIcon from '@/assets/svgs/drop.svg';
import { DownloadIcon, CloseIcon } from "@/assets/svgs/icons";


type SampleFileColumn = {
    attribute: string;
    description?: string;
}

interface UploadModalTitleProps {
    label: string,
    sampleFileName?: string,
    sampleFileColumns?: SampleFileColumn[]
}

export const UploadModalTitle: React.FC<UploadModalTitleProps> = (props) => {
    const { label, sampleFileName, sampleFileColumns } = props;
    const intl = useIntl();

    const getExcelColumnWidth = (text: string) => {
        let width = 0;
        for (const ch of text) {
            if (isCJKCharacter(ch)) {
                width += 1.5;
            } else {
                width += 1;
            }
        }
        return width;
    };

    const onClickDownload = (sampleFileName?: string, sampleFileColumns?: SampleFileColumn[]) => {
        if (!sampleFileColumns || !sampleFileName) {
            return;
        }

        const ws = XLSX.utils.json_to_sheet([
            sampleFileColumns.reduce((acc, column) => {
                return {
                    ...acc,
                    [column.attribute]: column.description ?? ""
                };
            }, {})
        ]);
        ws["!cols"] = sampleFileColumns.map(column => ({ wch: getExcelColumnWidth(column.description ?? column.attribute) }));
        ws["!rows"] = Array(XLSX.utils.sheet_to_json(ws, { header: 1 }).length).fill({ hpt: 16 });

        const data = new Blob([
            XLSX.write({
                Sheets: { data: ws },
                SheetNames: ["data"]
            }, {
                bookType: "xlsx",
                type: "array"
            })
        ], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        });
        FileSaver.saveAs(data, `${sampleFileName}.xlsx`);
    };

    return (
        <div className="flex gap-2 items-end">
            <div className="py-1 font-[600] text-[16px] leading-none text-black-500">
                {label}
            </div>
            {sampleFileColumns && sampleFileName && (
                <button className="py-1 flex items-center gap-[3px] text-chat42 font-[400] text-[12px] leading-none"
                        onClick={() => onClickDownload(sampleFileName, sampleFileColumns)}
                >
                    <DownloadIcon className="size-[13px]"
                                    aria-hidden="true"
                    />
                    {intl.formatMessage({ id: "i00002" })}
                </button>
            )}
        </div>
    );
};


const FILE_UPLOAD_MAX_SIZE = 20971520; // 20MB

const EXCEL_FILE_ACCEPT = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xlx"]
};

interface FileUploadZoneProps {
    file: File | null
    onChangeFile: (file: File | null) => void
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = (props) => {
    const { file, onChangeFile } = props;

    const intl = useIntl();

    const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (acceptedFiles.length > 0) {
            onChangeFile(acceptedFiles[0]);
            return;
        }

        if (rejectedFiles.length > 0) {
            const { errors } = rejectedFiles[0];
            const { code } = errors[0];
            if (code === ErrorCode.FileInvalidType) {
                errorToast(intl.formatMessage({ id: "i00004" }));
            } else if (code === ErrorCode.FileTooLarge) {
                errorToast(intl.formatMessage({ id: "i00005" }));
            } else if (code === ErrorCode.TooManyFiles) {
                errorToast(intl.formatMessage({ id: "i00022" }));
            } else {
                errorToast(intl.formatMessage({ id: "i00006" }));
            }
        }
    }, [intl, onChangeFile]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: EXCEL_FILE_ACCEPT,
        maxSize: FILE_UPLOAD_MAX_SIZE
    });

    return (
        <div className="w-full">
            {file ? (
                <div className="w-full p-4 flex justify-between items-center gap-4 border border-black-200 rounded-[4px]">
                    <div className="flex items-center gap-4">
                        <div className="size-12 flex justify-center items-center bg-chat42-5 border border-black-200 rounded-[6px]">
                            <div className="font-[400] text-[13px] leading-[1.5] text-black-500">
                                {`.${file.name.split('.').slice(-1)[0].toUpperCase()}`}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="font-[500] text-[14px] leading-[1.5] text-black-500">
                                {file.name}
                            </div>
                            <div className="font-[400] text-[12px] leading-[1.5] text-black-400">
                                {bytes(file.size)}
                            </div>
                        </div>
                    </div>
                    <button aria-label={intl.formatMessage({ id: "b0003" })}
                            onClick={() => onChangeFile(null)}
                    >
                        <CloseIcon className="size-4"
                                   aria-hidden="true"
                        />
                    </button>
                </div>
            ) : (
                <div {...getRootProps()}>
                    <input {...getInputProps({ accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })} />
                    <div className="w-full p-14 border border-black-300 border-dashed bg-black-100 cursor-pointer">
                        <form method="post">
                            <div className="flex justify-center items-center gap-10">
                                <img src={FileDropIcon}
                                     alt=""
                                     aria-hidden="true"
                                     className="size-[52px]"
                                />

                                <div className="flex flex-col gap-4">
                                    <div className="font-[300] text-[16px] text-black-500 leading-[1.5]">
                                        <span>
                                            {intl.formatMessage({ id: "i00003_1" })}
                                        </span>
                                        <span className="text-chat42 font-[500] underline">
                                            {intl.formatMessage({ id: "i00003_2" })}
                                        </span>
                                        <span>
                                            {intl.formatMessage({ id: "i00003_3" })}
                                        </span>
                                    </div>
                                    <div className="whitespace-pre-wrap font-[300] text-[12px] text-black-400 leading-[1.4]">
                                        {intl.formatMessage({ id: "i00003" })}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
